import React from "react";
import {
  X,
  Check,
  ArrowLeft,
  Calendar,
  ClipboardList,
  Layout,
  UserCog,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useExhibitorStore } from "../../../store/useExhibitorStore";
import SizeShow from "../../booths/SizeShow";
import ExhibitorReqFormField from "../../exhibitor/exhibitor-request/ExhibitorReqFormField";

export default function ApplyWizardModal({
  categories,
  eventData,
  formatDateRange,
  handleApplySubmit,
  renderBenefit,
}) {
  const isApplying = useExhibitorStore((state) => state.isApplying);
  const setIsApplying = useExhibitorStore((state) => state.setIsApplying);
  const applyStep = useExhibitorStore((state) => state.applyStep);
  const setApplyStep = useExhibitorStore((state) => state.setApplyStep);
  const selectedCategory = useExhibitorStore((state) => state.selectedCategory);
  const setSelectedCategory = useExhibitorStore((state) => state.setSelectedCategory);
  const applyFormData = useExhibitorStore((state) => state.applyFormData);
  const applyFormErrors = useExhibitorStore((state) => state.applyFormErrors);
  const setApplyFormErrors = useExhibitorStore((state) => state.setApplyFormErrors);
  const formFields = useExhibitorStore((state) => state.formFields);
  const submittingApply = useExhibitorStore((state) => state.submittingApply);
  const selectedBooth = useExhibitorStore((state) => state.selectedBooth);
  const updateFormField = useExhibitorStore((state) => state.updateFormField);
  const isSubmitted = useExhibitorStore((state) => state.isSubmitted);
  const resetApplyForm = useExhibitorStore((state) => state.resetApplyForm);
  
  const validateFile = (file, isImage = false) => {
    if (!file) return null;
    const fileName = file.name;
    const lastDotIndex = fileName.lastIndexOf(".");
    const extension = lastDotIndex !== -1 ? fileName.slice(lastDotIndex).toLowerCase() : "";
    const namePart = lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName;
    
    // Type validation
    if (isImage) {
      const allowedExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
      if (!allowedExtensions.includes(extension)) {
        return "Invalid file type. Only JPG, JPEG, PNG, GIF, and WEBP are allowed.";
      }
    }

    // Name validation
    const illegalChars = /[ _.,@-]/; 
    if (illegalChars.test(namePart)) {
      return "File name should not contain spaces, underscores, dots, commas, @, or hyphens. Please rename the file.";
    }
    return null;
  };

  const onClose = () => resetApplyForm();

  if (!isApplying) return null;

  const ThemeColor = "#6ebf8b";

  return (
    <div className="fixed inset-0 z-[60] bg-white flex flex-col md:flex-row items-stretch overflow-y-auto md:overflow-hidden animate-in fade-in zoom-in duration-200">
      {/* Sidebar (Left) - Responsive Height/Width */}
      <div 
        className="w-full md:w-[320px] lg:w-[360px] flex-shrink-0 text-white px-6 py-4 md:p-10 flex flex-col items-start shadow-xl z-20 md:min-h-screen"
        style={{ backgroundColor: ThemeColor }}
      >
        <span className="text-[11px] font-black uppercase tracking-[1px] text-white/80 mb-2 block">
          IN PERSON EVENT
        </span>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 md:mb-8 leading-tight">
          {eventData?.event_name || "new event"}
        </h1>
        <div className="flex items-center gap-3 text-base md:text-lg font-medium opacity-90 mb-4 md:mb-6">
          <Calendar size={20} className="opacity-80" />
          <span>
            {formatDateRange(eventData?.start_date, eventData?.end_date)}
          </span>
        </div>
      </div>

      {/* Right Side Container (Header, Content, Footer) */}
      <div className="flex-1 flex flex-col min-w-0 md:overflow-hidden bg-white">
        {/* Header Progress Bar */}
        <div className="h-[72px] flex-shrink-0 border-b border-gray-100 flex items-center justify-center relative bg-white px-16 my-4 md:my-0">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
          >
            <X size={22} />
          </button>

          {/* Stepper */}
          <div className="flex items-start gap-0 font-sans">

            {/* Step 1 */}
            <div className="flex flex-col items-center gap-2">
              <motion.div
                animate={{
                  backgroundColor: (applyStep >= 1 || isSubmitted) ? ThemeColor : "white",
                  borderColor: (applyStep >= 1 || isSubmitted) ? ThemeColor : "#e5e7eb",
                  scale: applyStep === 1 && !isSubmitted ? 1.12 : 1,
                }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                className="w-9 h-9 rounded-full border-[3px] flex items-center justify-center shadow-sm"
              >
                <AnimatePresence mode="wait">
                  {(applyStep > 1 || isSubmitted) ? (
                    <motion.span
                      key="check1"
                      initial={{ scale: 0, opacity: 0, rotate: -30 }}
                      animate={{ scale: 1, opacity: 1, rotate: 0 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                      className="text-white"
                    >
                      <Check size={16} strokeWidth={3.5} />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="dot1"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                      className="w-2 h-2 rounded-full bg-white block"
                    />
                  )}
                </AnimatePresence>
              </motion.div>
              <motion.span
                animate={{ color: (applyStep >= 1 || isSubmitted) ? "#111827" : "#9ca3af" }}
                transition={{ duration: 0.3 }}
                className="text-[11px] font-bold whitespace-nowrap"
              >
                Select Category
              </motion.span>
            </div>

            {/* Line 1 → 2 */}
            <div className="relative w-[60px] sm:w-[90px] md:w-[120px] h-[3px] bg-gray-100 rounded-full mx-2 overflow-hidden mt-[16px]">
              <motion.div
                initial={{ width: "0%" }}
                animate={{ width: (applyStep > 1 || isSubmitted) ? "100%" : "0%" }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                className="absolute inset-0 h-full rounded-full"
                style={{ backgroundColor: ThemeColor }}
              />
            </div>

            {/* Step 2 */}
            <div className="flex flex-col items-center gap-2">
              <motion.div
                animate={{
                  backgroundColor: (applyStep >= 2 || isSubmitted) ? ThemeColor : "white",
                  borderColor: (applyStep >= 2 || isSubmitted) ? ThemeColor : "#e5e7eb",
                  scale: applyStep === 2 && !isSubmitted ? 1.12 : 1,
                }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                className="w-9 h-9 rounded-full border-[3px] flex items-center justify-center shadow-sm"
              >
                <AnimatePresence mode="wait">
                  {isSubmitted ? (
                    <motion.span
                      key="check2-submitted"
                      initial={{ scale: 0, opacity: 0, rotate: -30 }}
                      animate={{ scale: 1, opacity: 1, rotate: 0 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20, delay: 0.15 }}
                      className="text-white"
                    >
                      <Check size={16} strokeWidth={3.5} />
                    </motion.span>
                  ) : applyStep === 2 ? (
                    <motion.span
                      key="dot2"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                      className="w-2 h-2 rounded-full bg-white block"
                    />
                  ) : (
                    <motion.span
                      key="empty2"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-2.5 h-2.5 rounded-full bg-gray-200 block"
                    />
                  )}
                </AnimatePresence>
              </motion.div>
              <motion.span
                animate={{ color: (applyStep >= 2 || isSubmitted) ? "#111827" : "#9ca3af" }}
                transition={{ duration: 0.3 }}
                className="text-[11px] font-bold whitespace-nowrap"
              >
                Share Details
              </motion.span>
            </div>

            {/* Line 2 → 3 */}
            <div className="relative w-[60px] sm:w-[90px] md:w-[120px] h-[3px] bg-gray-100 rounded-full mx-2 overflow-hidden mt-[16px]">
              <motion.div
                initial={{ width: "0%" }}
                animate={{ width: isSubmitted ? "100%" : "0%" }}
                transition={{ duration: 0.6, ease: "easeInOut", delay: 0.3 }}
                className="absolute inset-0 h-full rounded-full"
                style={{ backgroundColor: ThemeColor }}
              />
            </div>

            {/* Step 3 — Submitted */}
            <div className="flex flex-col items-center gap-2">
              <motion.div
                animate={{
                  backgroundColor: isSubmitted ? ThemeColor : "white",
                  borderColor: isSubmitted ? ThemeColor : "#e5e7eb",
                  scale: isSubmitted ? 1.12 : 1,
                }}
                transition={{ type: "spring", stiffness: 300, damping: 22, delay: isSubmitted ? 0.4 : 0 }}
                className="w-9 h-9 rounded-full border-[3px] flex items-center justify-center shadow-sm"
              >
                <AnimatePresence mode="wait">
                  {isSubmitted ? (
                    <motion.span
                      key="check3"
                      initial={{ scale: 0, opacity: 0, rotate: -30 }}
                      animate={{ scale: 1, opacity: 1, rotate: 0 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20, delay: 0.45 }}
                      className="text-white"
                    >
                      <Check size={16} strokeWidth={3.5} />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="empty3"
                      animate={{ scale: 1 }}
                      className="w-2.5 h-2.5 rounded-full bg-gray-200 block"
                    />
                  )}
                </AnimatePresence>
              </motion.div>
              <motion.span
                animate={{ color: isSubmitted ? "#111827" : "#9ca3af" }}
                transition={{ duration: 0.3 }}
                className="text-[11px] font-bold whitespace-nowrap"
              >
                Submitted
              </motion.span>
            </div>

          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 md:overflow-y-auto custom-scrollbar bg-white font-sans">
          {isSubmitted ? (
            <div className="flex flex-col min-h-full">

              {/* Hero Section */}
              <div className="flex flex-col items-center justify-center text-center px-6 py-10 md:py-14 max-w-lg mx-auto w-full">
                {/* Animated check circle */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.5 }}
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center mb-6 md:mb-8"
                  style={{ backgroundColor: `${ThemeColor}15`, color: ThemeColor }}
                >
                  <motion.div
                    initial={{ scale: 0, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 18, delay: 0.65 }}
                  >
                    <Check size={38} strokeWidth={3} />
                  </motion.div>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.4 }}
                  className="text-2xl md:text-3xl font-black text-gray-900 mb-3 tracking-tight"
                >
                  Request Sent!
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                  className="text-gray-500 text-sm md:text-base leading-relaxed font-medium mb-8"
                >
                  The event organizer will review your application and reach out via email with next steps.
                </motion.p>

                <motion.button
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.4 }}
                  onClick={onClose}
                  className="w-full md:w-auto px-10 py-3.5 rounded-lg text-[13px] font-black uppercase tracking-[2px] text-white shadow-md hover:opacity-90 transition-all active:scale-[0.98]"
                  style={{ backgroundColor: ThemeColor }}
                >
                  Back to Home
                </motion.button>
              </div>

              {/* What's Next */}
              <div className="border-t border-dashed border-gray-200 bg-gray-50/30 px-6 md:px-8 py-8 md:py-10 pb-16 md:pb-20 flex-1">
                <h3 className="text-center text-lg font-black text-gray-900 mb-8 md:mb-12 uppercase tracking-wide">What's Next</h3>

                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12">

                  <motion.div
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0, duration: 0.4 }}
                    className="flex gap-4 md:flex-col md:items-center md:text-center group"
                  >
                    <div className="flex-shrink-0 p-3 md:p-4 rounded-2xl bg-white shadow-sm text-blue-500 md:mb-6 group-hover:scale-110 transition-transform self-start md:self-auto">
                      <ClipboardList size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 md:mb-3 text-sm md:text-lg">Application Review</h4>
                      <p className="text-[12px] md:text-[13px] text-gray-500 leading-relaxed md:font-medium">
                        During the review process the event team will evaluate your request in order to approve it
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.12, duration: 0.4 }}
                    className="flex gap-4 md:flex-col md:items-center md:text-center md:border-x md:border-gray-100 md:px-8 group"
                  >
                    <div className="flex-shrink-0 p-3 md:p-4 rounded-2xl bg-white shadow-sm text-blue-500 md:mb-6 group-hover:scale-110 transition-transform self-start md:self-auto">
                      <Layout size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 md:mb-3 text-sm md:text-lg">Access Exhibitor Space</h4>
                      <p className="text-[12px] md:text-[13px] text-gray-500 leading-relaxed md:font-medium">
                        Once your request is approved you'll get an email to access the Exhibitor Space on the website where you can manage booth members
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.24, duration: 0.4 }}
                    className="flex gap-4 md:flex-col md:items-center md:text-center group"
                  >
                    <div className="flex-shrink-0 p-3 md:p-4 rounded-2xl bg-white shadow-sm text-blue-500 md:mb-6 group-hover:scale-110 transition-transform self-start md:self-auto">
                      <UserCog size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 md:mb-3 text-sm md:text-lg">Manage Exhibitor Profile</h4>
                      <p className="text-[12px] md:text-[13px] text-gray-500 leading-relaxed md:font-medium">
                        Keep your exhibitor page up-to-date by managing your profile information and uploading necessary materials for booth visitors
                      </p>
                    </div>
                  </motion.div>

                </div>
              </div>

            </div>
          ) : applyStep === 1 ? (
            <div className="p-4 md:p-12 max-w-4xl mx-auto flex flex-col md:flex-row gap-6 md:gap-12">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-6">
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 flex-shrink-0"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Exhibitor Category</h2>
                </div>
                <div className="space-y-3">
                  {categories.map((cat) => {
                    const isSelected = selectedCategory?.id === cat.id;
                    const priceLabel = cat.priceType === "free" || Number(cat.price) === 0
                      ? "Free"
                      : `${cat.currency}${Number(cat.price).toLocaleString()}`;
                    return (
                      <div key={cat.id}>
                        {/* Category Card */}
                        <div
                          onClick={() => setSelectedCategory(cat)}
                          className={`p-4 border-2 rounded-xl flex items-center justify-between cursor-pointer transition-all duration-200 ${isSelected ? "bg-gray-50/50 shadow-sm" : "border-gray-100 hover:border-gray-200 bg-white"}`}
                          style={{ borderColor: isSelected ? ThemeColor : "#f3f4f6" }}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
                              style={{ borderColor: isSelected ? ThemeColor : "#d1d5db", backgroundColor: isSelected ? ThemeColor : "white" }}
                            >
                              {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            <div>
                              <span className="text-sm font-semibold text-gray-800 tracking-tight block">{cat.name}</span>
                              <span className="text-[11px] text-gray-400 font-medium md:hidden">
                                {cat.boothLength} × {cat.boothBreadth} {cat.unit || "ft"}
                              </span>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-gray-900 flex-shrink-0 ml-2">{priceLabel}</span>
                        </div>

                        {/* Inline Benefits — mobile only, below selected card */}
                        {isSelected && (
                          <div className="md:hidden mt-2 mb-1 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 animate-in fade-in slide-in-from-top-2 duration-200">
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-2">Benefits</p>
                            <div className="space-y-1">
                              {renderBenefit(
                                true,
                                <span className="inline-flex whitespace-pre-wrap">Booth Dimensions: <SizeShow height={cat.boothLength} width={cat.boothBreadth} booth_dimension_unit={cat.unit} /></span>
                              )}
                              {renderBenefit(true, "Booth Members", cat.boothMembers)}
                              {cat.features
                                ?.filter((f) => f.visible !== 0 && f.is_visible !== 0 && f.isVisible !== false)
                                .sort((a, b) => {
                                  const isAEnabled =
                                    a.enabled === 1 ||
                                    a.enabled === true ||
                                    a.is_enabled === 1 ||
                                    String(a.value).toLowerCase() === "yes" ||
                                    (a.value !== null && a.value !== undefined && !["no", "0"].includes(String(a.value).toLowerCase()));

                                  const isBEnabled =
                                    b.enabled === 1 ||
                                    b.enabled === true ||
                                    b.is_enabled === 1 ||
                                    String(b.value).toLowerCase() === "yes" ||
                                    (b.value !== null && b.value !== undefined && !["no", "0"].includes(String(b.value).toLowerCase()));

                                  if (isAEnabled !== isBEnabled) {
                                    return isAEnabled ? -1 : 1;
                                  }
                                  return (a.order || a.display_order || 0) - (b.order || b.display_order || 0);
                                })
                                .map((feature) => {
                                  const isEnabled =
                                    feature.enabled === 1 ||
                                    feature.enabled === true ||
                                    feature.is_enabled === 1 ||
                                    String(feature.value).toLowerCase() === "yes" ||
                                    (feature.value !== null && feature.value !== undefined && !["no", "0"].includes(String(feature.value).toLowerCase()));

                                  const isNumericCount =
                                    feature.value !== null &&
                                    feature.value !== undefined &&
                                    !["yes", "no"].includes(String(feature.value).toLowerCase()) &&
                                    !isNaN(Number(feature.value)) &&
                                    Number(feature.value) > 0;

                                  const qty = isNumericCount ? feature.value : null;

                                  return (
                                    <div key={feature.id || feature.feature_id || feature.key}>
                                      {renderBenefit(
                                        isEnabled,
                                        feature.name,
                                        qty,
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Desktop Benefits Sidebar */}
              <div className="hidden md:block w-full md:w-[300px] bg-gray-50/50 border border-gray-100 rounded-2xl p-6 md:p-8 h-fit self-start md:sticky md:top-0">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-[1.5px] mb-6 border-b border-gray-200 pb-4">
                  Benefits
                </h3>
                {selectedCategory ? (
                  <div className="space-y-1 animate-in fade-in slide-in-from-right-4 duration-300">
                    {renderBenefit(
                      true,
                      <span className="inline-flex whitespace-pre-wrap">Booth Dimensions: <SizeShow height={selectedCategory.boothLength} width={selectedCategory.boothBreadth} booth_dimension_unit={selectedCategory.unit} /></span>
                    )}
                    {renderBenefit(
                      true,
                      "Booth Members",
                      selectedCategory.boothMembers,
                    )}
                    {selectedCategory.features
                        ?.filter((f) => f.visible !== 0 && f.is_visible !== 0 && f.isVisible !== false)
                        .sort((a, b) => {
                          const isAEnabled =
                            a.enabled === 1 ||
                            a.enabled === true ||
                            a.is_enabled === 1 ||
                            String(a.value).toLowerCase() === "yes" ||
                            (a.value !== null && a.value !== undefined && !["no", "0"].includes(String(a.value).toLowerCase()));

                          const isBEnabled =
                            b.enabled === 1 ||
                            b.enabled === true ||
                            b.is_enabled === 1 ||
                            String(b.value).toLowerCase() === "yes" ||
                            (b.value !== null && b.value !== undefined && !["no", "0"].includes(String(b.value).toLowerCase()));

                          if (isAEnabled !== isBEnabled) {
                            return isAEnabled ? -1 : 1;
                          }
                          return (a.order || a.display_order || 0) - (b.order || b.display_order || 0);
                        })
                        .map((feature) => {
                          const isEnabled =
                            feature.enabled === 1 ||
                            feature.enabled === true ||
                            feature.is_enabled === 1 ||
                            String(feature.value).toLowerCase() === "yes" ||
                            (feature.value !== null && feature.value !== undefined && !["no", "0"].includes(String(feature.value).toLowerCase()));

                          const isNumericCount =
                            feature.value !== null &&
                            feature.value !== undefined &&
                            !["yes", "no"].includes(String(feature.value).toLowerCase()) &&
                            !isNaN(Number(feature.value)) &&
                            Number(feature.value) > 0;

                          const qty = isNumericCount ? feature.value : null;

                          return (
                            <div key={feature.id || feature.feature_id || feature.key}>
                              {renderBenefit(
                                isEnabled,
                                feature.name,
                                qty,
                              )}
                            </div>
                          );
                        })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 font-medium italic text-center py-4">
                    Please select a category to view its benefits
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-12 max-w-3xl mx-auto pb-6">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-6">
                  {!selectedBooth && (
                    <button
                      onClick={() => setApplyStep(1)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 flex-shrink-0"
                    >
                      <ArrowLeft size={20} />
                    </button>
                  )}
                  <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">
                    Share Details
                  </h2>
                </div>

                {/* Form card: borderless on mobile, card on desktop */}
                <div className="space-y-6 md:bg-white md:border md:border-gray-100 md:px-8 md:py-8 md:rounded-2xl md:shadow-sm">
                  {Array.from(new Set(formFields.map(f => f.section || "General Information"))).map((sectionName) => {
                    const sectionFields = formFields.filter(f => (f.section || "General Information") === sectionName);
                    if (sectionFields.length === 0) return null;

                    return (
                      <div key={sectionName} className="space-y-5">
                        {/* Section divider */}
                        <div className="flex items-center gap-3">
                          <div className="h-px flex-1 bg-gray-100"></div>
                          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] whitespace-nowrap">
                            {sectionName}
                          </h3>
                          <div className="h-px flex-1 bg-gray-100"></div>
                        </div>

                        <div className="space-y-5">
                          {sectionFields.map((field) => (
                            <ExhibitorReqFormField
                              key={field.id}
                              field={field}
                              value={applyFormData[field.id]}
                              error={applyFormErrors[field.id]}
                              onChange={(id, val) => {
                                updateFormField(id, val);
                                if (applyFormErrors[id] || applyFormErrors.apiError) {
                                  const newErrors = { ...applyFormErrors };
                                  delete newErrors[id];
                                  delete newErrors.apiError;
                                  setApplyFormErrors(newErrors);
                                }
                              }}
                              onFileChange={(id, file) => {
                                if (file) {
                                  const isImage = field.type?.toLowerCase() === "image" || field.field_type?.toLowerCase() === "image";
                                  const fileError = validateFile(file, isImage);
                                  if (fileError) {
                                    setApplyFormErrors({
                                      ...applyFormErrors,
                                      [id]: fileError
                                    });
                                    return;
                                  }
                                }
                                updateFormField(id, file);
                                if (applyFormErrors[id] || applyFormErrors.apiError) {
                                  const newErrors = { ...applyFormErrors };
                                  delete newErrors[id];
                                  delete newErrors.apiError;
                                  setApplyFormErrors(newErrors);
                                }
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Footer Navigation Bar */}
        {!isSubmitted && (
          <>
            {/* DESKTOP Footer */}
            <div className="hidden md:flex min-h-24 py-6 md:py-0 flex-shrink-0 border-t border-gray-100 bg-white px-6 md:px-12 flex-col md:flex-row items-center justify-between gap-6 md:gap-0 z-20 w-full">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 md:gap-10">
                {selectedBooth && (
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">BOOTH NUMBER</span>
                    <span className="text-sm font-bold text-gray-900 tracking-tight">{selectedBooth.data?.number || selectedBooth.data?.label}</span>
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">CATEGORY</span>
                  <span className="text-sm font-bold text-gray-900 tracking-tight">{selectedCategory?.name || "--"}</span>
                </div>
                {selectedBooth && (
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">BOOTH PRICE</span>
                    <span className="text-sm font-bold text-gray-900 tracking-tight">{selectedBooth.data?.price || 0}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-3">
                {applyFormErrors.apiError && (
                  <div className="bg-red-50 border border-red-100 text-red-500 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest animate-in fade-in slide-in-from-bottom-2 shadow-sm">
                    {applyFormErrors.apiError}
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <button onClick={onClose} className="px-8 py-3 rounded-md text-xs font-bold uppercase tracking-[1.5px] text-gray-500 hover:bg-gray-50 transition-all">
                    Cancel
                  </button>
                  <button
                    disabled={submittingApply || (applyStep === 1 && !selectedCategory)}
                    onClick={() => { if (applyStep === 1) setApplyStep(2); else handleApplySubmit(); }}
                    className="bg-black text-white px-12 py-3 rounded-md text-xs font-bold uppercase tracking-[1.5px] hover:bg-gray-900 transition-all active:scale-[0.98] disabled:opacity-30 disabled:pointer-events-none shadow-xl"
                  >
                    {applyStep === 1 ? "CONTINUE" : submittingApply ? "SUBMITTING..." : "APPLY TO EXHIBIT"}
                  </button>
                </div>
              </div>
            </div>

            {/* MOBILE Sticky Footer */}
            <div className="md:hidden sticky bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
              {/* Booth info strip */}
              {selectedBooth && (
                <div className="flex items-stretch divide-x divide-gray-200 bg-gray-50 border-b border-gray-200">
                  <div className="flex-1 px-4 py-2.5">
                    <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">BOOTH TYPE</span>
                    <span className="block text-[12px] font-bold text-gray-900 truncate">{selectedCategory?.name || "--"}</span>
                  </div>
                  <div className="flex-1 px-4 py-2.5">
                    <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">BOOTH SIZE</span>
                    <span className="block text-[12px] font-bold text-gray-900 truncate">
                      {selectedCategory ? `${selectedCategory.boothLength} x ${selectedCategory.boothBreadth} ${selectedCategory.unit || 'ft'}` : '--'}
                    </span>
                  </div>
                  <div className="flex-1 px-4 py-2.5">
                    <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">BOOTH PRICE</span>
                    <span className="block text-[12px] font-bold text-gray-900 truncate">
                      {selectedCategory
                        ? (selectedCategory.priceType === "free" || Number(selectedCategory.price) === 0 ? "Free" : `${selectedCategory.currency}${Number(selectedCategory.price).toLocaleString()}`)
                        : '--'}
                    </span>
                  </div>
                </div>
              )}
              {!selectedBooth && selectedCategory && (
                <div className="flex items-stretch divide-x divide-gray-200 bg-gray-50 border-b border-gray-200">
                  <div className="flex-1 px-4 py-2.5">
                    <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">BOOTH TYPE</span>
                    <span className="block text-[12px] font-bold text-gray-900 truncate">{selectedCategory.name}</span>
                  </div>
                  <div className="flex-1 px-4 py-2.5">
                    <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">BOOTH SIZE</span>
                    <span className="block text-[12px] font-bold text-gray-900 truncate">
                      {`${selectedCategory.boothLength} x ${selectedCategory.boothBreadth} ${selectedCategory.unit || 'ft'}`}
                    </span>
                  </div>
                  <div className="flex-1 px-4 py-2.5">
                    <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">BOOTH PRICE</span>
                    <span className="block text-[12px] font-bold text-gray-900 truncate">
                      {selectedCategory.priceType === "free" || Number(selectedCategory.price) === 0 ? "Free" : `${selectedCategory.currency}${Number(selectedCategory.price).toLocaleString()}`}
                    </span>
                  </div>
                </div>
              )}
              {/* Action buttons row */}
              {applyFormErrors.apiError && (
                <div className="px-4 pt-2 text-[10px] font-black uppercase tracking-widest text-red-500 bg-red-50 border-b border-red-100">
                  {applyFormErrors.apiError}
                </div>
              )}
              <div className="flex items-center gap-3 px-4 py-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 rounded-lg text-xs font-bold uppercase tracking-[1.5px] text-gray-500 bg-gray-100 hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  disabled={submittingApply || (applyStep === 1 && !selectedCategory)}
                  onClick={() => { if (applyStep === 1) setApplyStep(2); else handleApplySubmit(); }}
                  className="flex-[2] py-3 rounded-lg text-xs font-bold uppercase tracking-[1.5px] text-white transition-all active:scale-[0.98] disabled:opacity-30 disabled:pointer-events-none"
                  style={{ backgroundColor: submittingApply ? '#9ca3af' : '#000' }}
                >
                  {applyStep === 1 ? "CONTINUE" : submittingApply ? "SUBMITTING..." : "APPLY TO EXHIBIT"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
